import * as React from "react";
import { UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";

interface FileDropZoneProps {
  /** Accepted file extensions, e.g. ".docx" or ".xlsx,.xls,.csv". */
  accept: string;
  /** Called with the dropped/selected file. */
  onFile: (file: File) => void;
  /** Primary label shown in the zone. */
  label: string;
  /** Secondary hint line. */
  hint?: string;
  /** Disable the zone (e.g. while busy). */
  disabled?: boolean;
  className?: string;
}

/**
 * A clickable + drag-and-drop file input zone. Clicking opens the file picker;
 * dragging a file over highlights the zone and dropping it calls `onFile`.
 */
export function FileDropZone({
  accept,
  onFile,
  label,
  hint,
  disabled,
  className,
}: FileDropZoneProps) {
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors",
        dragging
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30",
        disabled && "cursor-not-allowed opacity-50",
        !disabled && "cursor-pointer",
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
      <UploadCloud
        className={cn("h-6 w-6", dragging ? "text-primary" : "text-muted-foreground")}
      />
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  );
}
